import { SECRET_ENVELOPE_PREFIX } from './scope.js';
import { describeVaultError, VaultHttp } from './vault-http.js';
/** A bare Transit ciphertext — what this cipher produced BEFORE scoping existed. */
const LEGACY_CIPHERTEXT_RE = /^vault:v\d+:/;
/** Any version of our own envelope, including ones this build predates. */
const ENVELOPE_RE = /^acv\d+:/;
export class VaultTransitSecretCipher {
    http;
    key;
    orgKeyPrefix;
    mount;
    openCacheMax;
    openCache = new Map();
    constructor(opts) {
        this.http = new VaultHttp({
            addr: opts.addr,
            namespace: opts.namespace,
            auth: opts.auth,
            fetchImpl: opts.fetchImpl,
            now: opts.now
        });
        this.key = opts.key;
        this.orgKeyPrefix = opts.orgKeyPrefix;
        this.mount = opts.mount ?? 'transit';
        this.openCacheMax = opts.openCacheMax ?? 5000;
    }
    /**
     * The key a scope resolves to. Org keys are created lazily by Transit on the
     * first encrypt (the policy must permit creation on that path); nothing here
     * is coupled to organization creation.
     */
    keyFor(scope) {
        return scope.kind === 'deployment' ? this.key : `${this.orgKeyPrefix}${scope.orgId}`;
    }
    async seal(plaintext, scope) {
        const data = await this.transit('encrypt', this.keyFor(scope), {
            plaintext: Buffer.from(plaintext, 'utf8').toString('base64')
        });
        if (typeof data.ciphertext !== 'string')
            throw new Error('vault transit encrypt: no ciphertext in response');
        return `${SECRET_ENVELOPE_PREFIX}${data.ciphertext}`;
    }
    async open(stored, scope) {
        // Two arms (docs/designs/per-org-secret-encryption.md §4):
        //   envelope-tagged ⇒ opens under THIS scope's key — a value of another
        //     scope fails here, which is the whole reason scope is a parameter;
        //   anything else ⇒ never sealed, return unchanged (pass-through contract).
        // Everything the pass-through must NOT swallow is rejected first.
        if (!stored.startsWith(SECRET_ENVELOPE_PREFIX)) {
            assertNotUnreadableCiphertext(stored);
            return stored;
        }
        const key = this.keyFor(scope);
        const ciphertext = stored.slice(SECRET_ENVELOPE_PREFIX.length);
        // Cache by KEY + ciphertext: the stored string no longer implies a key, and
        // two scopes must never share an entry.
        const cacheKey = `${key}\u0000${ciphertext}`;
        const hit = this.openCache.get(cacheKey);
        if (hit !== undefined)
            return hit;
        const data = await this.transit('decrypt', key, { ciphertext });
        if (typeof data.plaintext !== 'string')
            throw new Error('vault transit decrypt: no plaintext in response');
        const value = Buffer.from(data.plaintext, 'base64').toString('utf8');
        this.cacheOpen(cacheKey, value);
        return value;
    }
    cacheOpen(ciphertext, plaintext) {
        if (this.openCache.size >= this.openCacheMax) {
            // Bounded, oldest-inserted-first — plenty for a stable working set.
            const oldest = this.openCache.keys().next().value;
            if (oldest !== undefined)
                this.openCache.delete(oldest);
        }
        this.openCache.set(ciphertext, plaintext);
    }
    async transit(op, key, body) {
        const res = await this.http.request('POST', `${this.mount}/${op}/${key}`, body);
        if (!res.ok)
            throw new Error(`vault transit ${op} failed: ${await describeVaultError(res)}`);
        const json = (await res.json());
        return json.data ?? {};
    }
}
/**
 * The pass-through arm exists for values that were never sealed (plaintext rows
 * under `SECRET_CIPHER=none`). It must never swallow a value that IS sealed but
 * unreadable by this build — returning ciphertext as if it were plaintext is
 * silent corruption: the caller ships it to a daemon or a platform API as a
 * credential. Both cases below are loud instead.
 */
function assertNotUnreadableCiphertext(stored) {
    if (LEGACY_CIPHERTEXT_RE.test(stored)) {
        // Sealed before scoping (docs/designs/per-org-secret-encryption.md §7). The
        // deployment-key fallback that used to read these is gone — it ignored the
        // asserted scope, which was a hole in the cross-tenant fence it only earned
        // by being temporary. Recovering such a value needs a pre-scoping build to
        // run the rewrap sweep first.
        throw new Error('secret cipher: unscoped legacy ciphertext — run the rewrap sweep on a pre-scoping build first');
    }
    if (ENVELOPE_RE.test(stored)) {
        // A NEWER envelope version than this build knows. Happens on a rolling
        // update where a new replica writes and an old one reads; failing here is
        // what keeps that window loud rather than silently wrong.
        throw new Error('secret cipher: stored value uses a newer envelope version than this build supports');
    }
}
//# sourceMappingURL=vault-transit.js.map